import React, { useState, useEffect } from 'react';
import { expenseService } from '../services/expenseService';
import { categoryService } from '../services/categoryService';
import ConfirmDialog from './ConfirmDialog';
import EditExpense from './EditExpense';

// Add CSS for spinning animation
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}

function ExpenseList({ expenses, categories = [], accounts = [], onExpenseUpdate, onExpenseDelete, onDownloadCSV, filters, selectedCategory }) {
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedExpenses, setSelectedExpenses] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [hierarchicalCategories, setHierarchicalCategories] = useState([]);

  // Clear selection when expenses change or filters are applied
  useEffect(() => {
    setSelectedExpenses(new Set());
    setLastSelectedIndex(null);
  }, [expenses, searchTerm, sortBy, sortOrder, selectedCategory]);

  // Load hierarchical categories for parent-child relationship checking
  useEffect(() => {
    const loadHierarchicalCategories = async () => {
      try {
        const hierarchicalData = await categoryService.getCategories();
        setHierarchicalCategories(hierarchicalData.categories || []);
      } catch (error) {
        console.error('Failed to load hierarchical categories:', error);
      }
    };
    
    loadHierarchicalCategories();
  }, []);

  // Helper to get bucket name from bucketId
  const getBucketName = (bucketId) => {
    if (!bucketId || !accounts.length) return null;
    for (const account of accounts) {
      if (account.buckets) {
        const bucket = account.buckets.find(b => b.id === bucketId);
        if (bucket) return bucket.name;
      }
    }
    return null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;

    try {
      setDeletingExpenseId(expenseToDelete.id);
      const user = JSON.parse(localStorage.getItem('user'));
      await expenseService.deleteExpense(expenseToDelete.id, user.familyId);

      // Update local state instead of refreshing
      onExpenseDelete(expenseToDelete.id);
      setExpenseToDelete(null);
    } catch (error) {
      console.error('Failed to delete expense:', error);
      alert('Failed to delete expense. Please try again.');
    } finally {
      setDeletingExpenseId(null);
    }
  };

  const handleCancelDelete = () => {
    setExpenseToDelete(null);
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedExpenses(new Set());
    setLastSelectedIndex(null);
  };

  const toggleExpenseSelection = (expenseId, event = null) => {
    const currentIndex = filteredAndSortedExpenses.findIndex(expense => expense.id === expenseId);

    // Handle shift-click for range selection
    if (event && event.shiftKey && lastSelectedIndex !== null && isSelectionMode) {
      const startIndex = Math.min(lastSelectedIndex, currentIndex);
      const endIndex = Math.max(lastSelectedIndex, currentIndex);

      const newSelected = new Set(selectedExpenses);

      // Select all expenses in the range
      for (let i = startIndex; i <= endIndex; i++) {
        newSelected.add(filteredAndSortedExpenses[i].id);
      }

      setSelectedExpenses(newSelected);
      // Don't update lastSelectedIndex on shift-click to allow extending selection
      return;
    }

    // Normal single selection
    const newSelected = new Set(selectedExpenses);
    if (newSelected.has(expenseId)) {
      newSelected.delete(expenseId);
    } else {
      newSelected.add(expenseId);
    }
    setSelectedExpenses(newSelected);
    setLastSelectedIndex(currentIndex);
  };

  const selectAllExpenses = () => {
    const allIds = new Set(filteredAndSortedExpenses.map(expense => expense.id));
    setSelectedExpenses(allIds);
    setLastSelectedIndex(null);
  };

  const clearSelection = () => {
    setSelectedExpenses(new Set());
    setLastSelectedIndex(null);
  };

  const handleBulkDelete = async () => {
    if (selectedExpenses.size === 0) return;

    const expenseIds = Array.from(selectedExpenses);
    const expenseNames = expenseIds.map(id => {
      const expense = expenses.find(e => e.id === id);
      return expense?.description || 'Unknown';
    }).slice(0, 3);

    const confirmMessage = expenseIds.length === 1
      ? `Are you sure you want to delete "${expenseNames[0]}"?`
      : `Are you sure you want to delete ${expenseIds.length} expenses? ${expenseNames.join(', ')}${expenseIds.length > 3 ? '...' : ''}`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setBulkDeleting(true);
      const user = JSON.parse(localStorage.getItem('user'));
      await expenseService.bulkDeleteExpenses(expenseIds, user.familyId);

      // Update local state by removing deleted expenses
      expenseIds.forEach(id => onExpenseDelete(id));

      setSelectedExpenses(new Set());
      setLastSelectedIndex(null);
      setIsSelectionMode(false);
    } catch (error) {
      console.error('Failed to delete expenses:', error);
      alert('Failed to delete expenses. Please try again.');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
  };

  const handleSaveEdit = async (updateData) => {
    if (!editingExpense) return;

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const response = await expenseService.updateExpense(editingExpense.id, updateData, user.familyId);

      // Handle different response formats
      let updatedExpense;
      if (response.body) {
        const responseBody = JSON.parse(response.body);
        updatedExpense = responseBody.expense;
      } else {
        updatedExpense = response.expense || response;
      }

      if (updatedExpense) {
        // Update local state instead of refreshing
        onExpenseUpdate(updatedExpense);
      } else {
        // Fallback: create updated expense from original + updateData
        updatedExpense = { ...editingExpense, ...updateData };
        onExpenseUpdate(updatedExpense);
      }

      setEditingExpense(null);
    } catch (error) {
      throw error; // Let EditExpense component handle the error
    }
  };

  const handleCancelEdit = () => {
    setEditingExpense(null);
  };

  const getCategoryInfo = (categoryName) => {
    const category = categories.find(cat => cat.name === categoryName);
    return {
      color: category?.color || '#95a5a6',
      icon: category?.icon || '📦',
      budgetLimit: category?.budgetLimit
    };
  };

  // Sort options
  const sortOptions = [
    { value: 'date', label: 'Date' },
    { value: 'description', label: 'Description' },
    { value: 'amount', label: 'Amount' },
    { value: 'category', label: 'Category' },
    { value: 'addedBy', label: 'Added By' }
  ];

  // Filter and sort expenses
  const filteredAndSortedExpenses = expenses
    .filter(expense => {
      // Apply search filter
      const matchesSearch = expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (expense.note && expense.note.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Apply category filter if selected
      let matchesCategory = true;
      if (selectedCategory) {
        // For split expenses, check if any split matches the selected category
        if (expense.splits && Array.isArray(expense.splits)) {
          matchesCategory = expense.splits.some(split => {
            // Check if split category matches directly
            if (split.categoryId === selectedCategory) {
              return true;
            }
            // Check if selected category is a parent and this split belongs to one of its subcategories
            const parentCategory = hierarchicalCategories.find(cat => 
              cat.name === selectedCategory && 
              cat.subcategories && 
              cat.subcategories.some(sub => sub.name === split.categoryId)
            );
            return !!parentCategory;
          });
        } else {
          // For simple expenses, check if the expense category matches directly
          if (expense.category === selectedCategory) {
            matchesCategory = true;
          } else {
            // Check if the selected category is a parent and this expense belongs to one of its subcategories
            const parentCategory = hierarchicalCategories.find(cat => 
              cat.name === selectedCategory && 
              cat.subcategories && 
              cat.subcategories.some(sub => sub.name === expense.category)
            );
            matchesCategory = !!parentCategory;
          }
        }
      }
      
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'description':
          aValue = a.description.toLowerCase();
          bValue = b.description.toLowerCase();
          break;
        case 'amount':
          aValue = Math.abs(a.amount);
          bValue = Math.abs(b.amount);
          break;
        case 'category':
          aValue = a.category.toLowerCase();
          bValue = b.category.toLowerCase();
          break;
        case 'addedBy':
          aValue = a.addedBy.toLowerCase();
          bValue = b.addedBy.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

  const getCategorySpending = (categoryName) => {
    // Use filtered and sorted expenses for budget calculations
    return filteredAndSortedExpenses
      .filter(expense => expense.category === categoryName)
      .reduce((total, expense) => total + expense.amount, 0);
  };

  const getBudgetStatus = (categoryName) => {
    const categoryInfo = getCategoryInfo(categoryName);
    if (!categoryInfo.budgetLimit) return null;

    const spent = getCategorySpending(categoryName);
    const budget = categoryInfo.budgetLimit;
    const percentage = (spent / budget) * 100;

    if (spent > budget) {
      return { type: 'over', percentage, spent, budget };
    } else if (percentage > 80) {
      return { type: 'warning', percentage, spent, budget };
    }
    return null;
  };

  const hasFilters = filters && Object.keys(filters).length > 0;
  const hasSearch = searchTerm.trim().length > 0;
  const hasCategoryFilter = selectedCategory !== null;

  if (expenses.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 40px',
        color: '#888',
        background: 'rgba(255, 107, 53, 0.05)',
        borderRadius: '12px',
        border: '2px dashed #404040'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>
          {hasFilters || hasCategoryFilter ? '🔍' : '💰'}
        </div>
        <p style={{ fontSize: '1.2rem', margin: '0' }}>
          {hasFilters || hasCategoryFilter
            ? 'No expenses found matching your filters. Try adjusting your search criteria.'
            : 'No expenses found. Add your first expense to get started!'
          }
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Search and Sort Controls */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '2fr 1fr auto auto',
          gap: '16px',
          alignItems: 'end'
        }}>
          {window.innerWidth <= 768 && (
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'space-between',
              alignItems: 'center',
              gridColumn: '1 / -1',
              order: 1,
              flexWrap: 'wrap'
            }}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid #404040',
                  borderRadius: '6px',
                  color: '#f0f0f0',
                  padding: '8px 12px',
                  fontSize: '12px',
                  minWidth: '120px'
                }}
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  style={{
                    background: 'rgba(255, 107, 53, 0.2)',
                    border: '1px solid #ff6b35',
                    color: '#ff6b35',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease'
                  }}
                  title={`Currently sorting ${sortOrder === 'asc' ? 'ascending' : 'descending'}. Click to toggle.`}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'} Sort
                </button>
                <button
                  onClick={() => onDownloadCSV && onDownloadCSV(filteredAndSortedExpenses)}
                  disabled={!onDownloadCSV || filteredAndSortedExpenses.length === 0}
                  style={{
                    background: 'rgba(52, 152, 219, 0.2)',
                    border: '1px solid #3498db',
                    color: filteredAndSortedExpenses.length === 0 ? '#666' : '#3498db',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: filteredAndSortedExpenses.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                    opacity: filteredAndSortedExpenses.length === 0 ? 0.5 : 1
                  }}
                  title={filteredAndSortedExpenses.length === 0 ? 'No expenses to download' : `Download ${filteredAndSortedExpenses.length} expense${filteredAndSortedExpenses.length !== 1 ? 's' : ''} as CSV`}
                >
                  📥 CSV
                </button>
              </div>
            </div>
          )}
          {/* Search Input */}
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label htmlFor="expense-search" style={{
              display: 'block',
              color: '#f0f0f0',
              marginBottom: '8px',
              fontWeight: '500',
              fontSize: '14px'
            }}>
              Search Expenses
            </label>
            <input
              id="expense-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search by description..."
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid #404040',
                borderRadius: '8px',
                color: '#f0f0f0',
                fontSize: '16px',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#ff6b35';
                e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#404040';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Sort By - Desktop only */}
          {window.innerWidth > 768 && (
            <div className="form-group" style={{ marginBottom: '0' }}>
              <label htmlFor="sort-by" style={{
                display: 'block',
                color: '#f0f0f0',
                marginBottom: '8px',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                Sort By
              </label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Sort Order Toggle - Desktop only */}
          {window.innerWidth > 768 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                style={{
                  background: 'rgba(255, 107, 53, 0.2)',
                  border: '1px solid #ff6b35',
                  color: '#ff6b35',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minHeight: '48px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 107, 53, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 107, 53, 0.2)';
                }}
                title={`Currently sorting ${sortOrder === 'asc' ? 'ascending' : 'descending'}. Click to toggle.`}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
                {window.innerWidth > 480 && (sortOrder === 'asc' ? 'A-Z' : 'Z-A')}
              </button>
            </div>
          )}

          {/* Download CSV Button - Desktop only */}
          {window.innerWidth > 768 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => onDownloadCSV && onDownloadCSV(filteredAndSortedExpenses)}
                disabled={!onDownloadCSV || filteredAndSortedExpenses.length === 0}
                style={{
                  background: 'rgba(52, 152, 219, 0.2)',
                  border: '1px solid #3498db',
                  color: filteredAndSortedExpenses.length === 0 ? '#666' : '#3498db',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: filteredAndSortedExpenses.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minHeight: '48px',
                  transition: 'all 0.2s ease',
                  opacity: filteredAndSortedExpenses.length === 0 ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (filteredAndSortedExpenses.length > 0) {
                    e.target.style.background = 'rgba(52, 152, 219, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (filteredAndSortedExpenses.length > 0) {
                    e.target.style.background = 'rgba(52, 152, 219, 0.2)';
                  }
                }}
                title={filteredAndSortedExpenses.length === 0 ? 'No expenses to download' : `Download ${filteredAndSortedExpenses.length} expense${filteredAndSortedExpenses.length !== 1 ? 's' : ''} as CSV`}
              >
                📥
                {window.innerWidth > 480 && 'CSV'}
              </button>
            </div>
          )}
        </div>

        {/* Search Results Info */}
        {hasSearch && (
          <div style={{
            marginTop: '12px',
            fontSize: '14px',
            color: '#b0b0b0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <span>
              <strong style={{ color: '#ff6b35' }}>{filteredAndSortedExpenses.length}</strong> of <strong>{expenses.length}</strong> expenses match "{searchTerm}"
            </span>
            <button
              onClick={() => setSearchTerm('')}
              style={{
                background: 'rgba(255, 107, 53, 0.2)',
                border: '1px solid #ff6b35',
                color: '#ff6b35',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Category Filter Info */}
        {selectedCategory && (
          <div style={{
            marginTop: '12px',
            fontSize: '14px',
            color: '#b0b0b0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
            padding: '12px',
            background: 'rgba(52, 152, 219, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(52, 152, 219, 0.2)'
          }}>
            <span>
              <strong style={{ color: '#3498db' }}>Category Filter:</strong> Showing <strong style={{ color: '#ff6b35' }}>{filteredAndSortedExpenses.length}</strong> expenses from "{selectedCategory}"
              {hierarchicalCategories.find(cat => cat.name === selectedCategory && cat.subcategories?.length > 0) && (
                <span style={{ color: '#888', fontSize: '12px' }}> (includes all subcategories)</span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Selection Controls */}
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '16px',
        background: isSelectionMode ? 'rgba(255, 107, 53, 0.1)' : 'rgba(255, 255, 255, 0.05)',
        borderRadius: '8px',
        border: `1px solid ${isSelectionMode ? 'rgba(255, 107, 53, 0.3)' : '#404040'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={toggleSelectionMode}
            style={{
              background: isSelectionMode ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255, 107, 53, 0.2)',
              border: '1px solid #ff6b35',
              color: '#ff6b35',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
          >
            {isSelectionMode ? '✓' : '☐'} {isSelectionMode ? 'Exit Selection' : 'Select Multiple'}
          </button>

          {isSelectionMode && (
            <>
              <span style={{ color: '#b0b0b0', fontSize: '14px' }}>
                {selectedExpenses.size} of {filteredAndSortedExpenses.length} selected
              </span>

              <span style={{ color: '#888', fontSize: '12px', fontStyle: 'italic' }}>
                💡 Hold Shift + click to select ranges
              </span>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={selectAllExpenses}
                  disabled={selectedExpenses.size === filteredAndSortedExpenses.length}
                  style={{
                    background: 'rgba(52, 152, 219, 0.2)',
                    border: '1px solid #3498db',
                    color: selectedExpenses.size === filteredAndSortedExpenses.length ? '#666' : '#3498db',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: selectedExpenses.size === filteredAndSortedExpenses.length ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    opacity: selectedExpenses.size === filteredAndSortedExpenses.length ? 0.5 : 1
                  }}
                >
                  Select All
                </button>

                <button
                  onClick={clearSelection}
                  disabled={selectedExpenses.size === 0}
                  style={{
                    background: 'rgba(149, 165, 166, 0.2)',
                    border: '1px solid #95a5a6',
                    color: selectedExpenses.size === 0 ? '#666' : '#95a5a6',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: selectedExpenses.size === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    opacity: selectedExpenses.size === 0 ? 0.5 : 1
                  }}
                >
                  Clear
                </button>
              </div>
            </>
          )}
        </div>

        {isSelectionMode && selectedExpenses.size > 0 && (
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            style={{
              background: 'rgba(255, 71, 87, 0.2)',
              border: '1px solid #ff4757',
              color: '#ff4757',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: bulkDeleting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: bulkDeleting ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {bulkDeleting ? (
              <>
                <span style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid transparent',
                  borderTop: '2px solid #ff4757',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Deleting...
              </>
            ) : (
              <>🗑️ Delete Selected ({selectedExpenses.size})</>
            )}
          </button>
        )}
      </div>

      {hasFilters && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          background: 'rgba(255, 107, 53, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 107, 53, 0.2)',
          fontSize: '14px',
          color: '#b0b0b0'
        }}>
          <strong style={{ color: '#ff6b35' }}>Filtered Results:</strong> {expenses.length} expense{expenses.length !== 1 ? 's' : ''} found
          <span style={{ color: '#888', marginLeft: '8px' }}>(showing all matching results)</span>
        </div>
      )}

      {!hasFilters && expenses.length >= 50 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          background: 'rgba(52, 152, 219, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(52, 152, 219, 0.2)',
          fontSize: '14px',
          color: '#b0b0b0'
        }}>
          <strong style={{ color: '#3498db' }}>Recent Expenses:</strong> Showing {expenses.length} most recent expenses
          <span style={{ color: '#888', marginLeft: '8px' }}>(use filters to see all expenses)</span>
        </div>
      )}

      {/* Show empty state for search results */}
      {filteredAndSortedExpenses.length === 0 && (hasSearch || hasCategoryFilter) && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#888',
          background: 'rgba(255, 107, 53, 0.05)',
          borderRadius: '12px',
          border: '2px dashed #404040',
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🔍</div>
          <p style={{ fontSize: '1.1rem', margin: '0 0 8px 0' }}>
            {hasSearch && hasCategoryFilter
              ? `No expenses found matching "${searchTerm}" in category "${selectedCategory}"`
              : hasSearch
                ? `No expenses found matching "${searchTerm}"`
                : `No expenses found in category "${selectedCategory}"`
            }
          </p>
          <p style={{ fontSize: '0.9rem', margin: '0', color: '#666' }}>
            Try a different search term or clear the filters to see all expenses.
          </p>
        </div>
      )}

      {filteredAndSortedExpenses.map((expense, index) => (
        <div
          key={expense.id}
          onClick={(event) => isSelectionMode && toggleExpenseSelection(expense.id, event)}
          style={{
            display: 'flex',
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
            padding: window.innerWidth <= 480 ? '12px' : window.innerWidth <= 768 ? '16px' : '20px',
            borderBottom: index === filteredAndSortedExpenses.length - 1 ? 'none' : '1px solid #404040',
            background: selectedExpenses.has(expense.id)
              ? 'rgba(255, 107, 53, 0.15)'
              : index % 2 === 0 ? 'rgba(255, 107, 53, 0.02)' : 'transparent',
            borderRadius: index === 0 ? '8px 8px 0 0' : index === filteredAndSortedExpenses.length - 1 ? '0 0 8px 8px' : '0',
            transition: 'all 0.2s ease',
            opacity: deletingExpenseId === expense.id ? 0.5 : 1,
            position: 'relative',
            gap: window.innerWidth <= 768 ? '12px' : '0',
            border: selectedExpenses.has(expense.id) ? '1px solid rgba(255, 107, 53, 0.3)' : 'none',
            cursor: isSelectionMode ? 'pointer' : 'default'
          }}
          onMouseEnter={(e) => {
            if (deletingExpenseId !== expense.id && !selectedExpenses.has(expense.id)) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 107, 53, 0.08)';
            }
          }}
          onMouseLeave={(e) => {
            if (deletingExpenseId !== expense.id) {
              e.currentTarget.style.backgroundColor = selectedExpenses.has(expense.id)
                ? 'rgba(255, 107, 53, 0.15)'
                : index % 2 === 0 ? 'rgba(255, 107, 53, 0.02)' : 'transparent';
            }
          }}
        >
          {/* Selection Checkbox */}
          {isSelectionMode && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: window.innerWidth <= 768 ? '0' : '16px',
              marginBottom: window.innerWidth <= 768 ? '8px' : '0'
            }}>
              <input
                type="checkbox"
                checked={selectedExpenses.has(expense.id)}
                onChange={(event) => toggleExpenseSelection(expense.id, event)}
                onClick={(e) => {
                  // Don't stop propagation for shift-clicks so range selection works
                  if (!e.shiftKey) {
                    e.stopPropagation();
                  }
                }}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#ff6b35',
                  cursor: 'pointer'
                }}
              />
            </div>
          )}

          <div style={{ flex: 1 }}>
            <h4 style={{
              margin: '0 0 ' + (expense.note ? '4px' : '8px') + ' 0',
              color: '#f0f0f0',
              fontSize: window.innerWidth <= 480 ? '1rem' : '1.1rem',
              fontWeight: '600'
            }}>
              {expense.description}
            </h4>
            {expense.note && (
              <p style={{
                margin: '0 0 8px 0',
                color: '#888',
                fontSize: window.innerWidth <= 480 ? '11px' : '12px',
                fontStyle: 'italic'
              }}>
                {expense.note.length > 20 ? expense.note.substring(0, 20) + '…' : expense.note}
              </p>
            )}
            <div style={{
              margin: '0',
              color: '#b0b0b0',
              fontSize: window.innerWidth <= 480 ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              {/* Display category badges - either single or multiple for splits */}
              {expense.splits && expense.splits.length > 0 ? (
                // Split expense - show all categories with amounts
                <>
                  <span style={{
                    background: 'rgba(255, 107, 53, 0.2)',
                    border: '1px solid rgba(255, 107, 53, 0.4)',
                    padding: window.innerWidth <= 480 ? '2px 8px' : '3px 10px',
                    borderRadius: '14px',
                    color: '#ff6b35',
                    fontSize: window.innerWidth <= 480 ? '11px' : '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 1px 3px rgba(255, 107, 53, 0.2)'
                  }}>
                    🔀 Split ({expense.splits.length})
                  </span>
                  {expense.splits.map((split, index) => {
                    const categoryInfo = getCategoryInfo(split.categoryId);
                    return (
                      <span
                        key={index}
                        style={{
                          background: `${categoryInfo.color}20`,
                          border: `1px solid ${categoryInfo.color}40`,
                          padding: window.innerWidth <= 480 ? '2px 8px' : '3px 10px',
                          borderRadius: '14px',
                          color: categoryInfo.color,
                          fontSize: window.innerWidth <= 480 ? '11px' : '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: `0 1px 3px ${categoryInfo.color}20`
                        }}
                      >
                        <span style={{ fontSize: window.innerWidth <= 480 ? '12px' : '14px' }}>{categoryInfo.icon}</span>
                        {split.categoryId}
                        <span style={{ 
                          fontSize: window.innerWidth <= 480 ? '10px' : '11px',
                          opacity: 0.8,
                          marginLeft: '2px'
                        }}>
                          ${Math.abs(split.amount).toFixed(2)}
                        </span>
                      </span>
                    );
                  })}
                </>
              ) : (
                // Simple expense - show single category
                <>
                  <span style={{
                    background: `${getCategoryInfo(expense.category).color}20`,
                    border: `1px solid ${getCategoryInfo(expense.category).color}40`,
                    padding: window.innerWidth <= 480 ? '2px 8px' : '3px 10px',
                    borderRadius: '14px',
                    color: getCategoryInfo(expense.category).color,
                    fontSize: window.innerWidth <= 480 ? '11px' : '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: `0 1px 3px ${getCategoryInfo(expense.category).color}20`
                  }}>
                    <span style={{ fontSize: window.innerWidth <= 480 ? '12px' : '14px' }}>{getCategoryInfo(expense.category).icon}</span>
                    {expense.category}
                  </span>
                  {(() => {
                    const budgetStatus = getBudgetStatus(expense.category);
                    if (budgetStatus) {
                      return (
                        <span style={{
                          background: budgetStatus.type === 'over' ? 'rgba(255, 71, 87, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                          border: `1px solid ${budgetStatus.type === 'over' ? 'rgba(255, 71, 87, 0.4)' : 'rgba(255, 193, 7, 0.4)'}`,
                          padding: window.innerWidth <= 480 ? '2px 6px' : '3px 8px',
                          borderRadius: '12px',
                          color: budgetStatus.type === 'over' ? '#ff4757' : '#ffc107',
                          fontSize: window.innerWidth <= 480 ? '10px' : '11px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px'
                        }}>
                          {budgetStatus.type === 'over' ? '⚠️' : '⚡'}
                          {budgetStatus.type === 'over' ? 'Over Budget' : 'Near Limit'}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
              <span>•</span>
              <span>{formatDate(expense.date)}</span>
              <span>•</span>
              <span>{expense.addedBy}</span>
              {expense.bucketId && getBucketName(expense.bucketId) && (
                <>
                  <span>•</span>
                  <span style={{
                    background: 'rgba(155, 89, 182, 0.2)',
                    border: '1px solid rgba(155, 89, 182, 0.4)',
                    padding: window.innerWidth <= 480 ? '2px 6px' : '3px 8px',
                    borderRadius: '12px',
                    color: '#9b59b6',
                    fontSize: window.innerWidth <= 480 ? '10px' : '11px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}>
                    🪣 {getBucketName(expense.bucketId)}
                  </span>
                </>
              )}
              {expense.entryMethod && expense.entryMethod !== 'manual' && (
                <>
                  <span>•</span>
                  <span style={{
                    background: 'rgba(52, 152, 219, 0.2)',
                    border: '1px solid rgba(52, 152, 219, 0.4)',
                    padding: window.innerWidth <= 480 ? '2px 6px' : '3px 8px',
                    borderRadius: '12px',
                    color: '#3498db',
                    fontSize: window.innerWidth <= 480 ? '10px' : '11px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                    title={expense.importTemplate ? `Imported via ${expense.importTemplate}` : 'Imported from CSV'}
                  >
                    📥 {expense.importTemplate || 'CSV'}
                  </span>
                </>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: window.innerWidth <= 768 ? 'space-between' : 'flex-end',
            gap: window.innerWidth <= 480 ? '8px' : '12px',
            flexWrap: window.innerWidth <= 480 ? 'wrap' : 'nowrap'
          }}>
            <div style={{
              textAlign: window.innerWidth <= 768 ? 'left' : 'right',
              order: window.innerWidth <= 768 ? 1 : 0
            }}>
              <span style={{
                fontSize: window.innerWidth <= 480 ? '1.1rem' : '1.3rem',
                fontWeight: '700',
                color: expense.amount > 0 ? '#ff4757' : '#2ed573',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}>
                {formatCurrency(Math.abs(expense.amount))}
              </span>
            </div>

            <div style={{
              display: 'flex',
              gap: window.innerWidth <= 480 ? '6px' : '8px',
              order: window.innerWidth <= 768 ? 2 : 0
            }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isSelectionMode && (
                <>
                  <button
                    onClick={() => handleEditClick(expense)}
                    style={{
                      background: 'rgba(255, 107, 53, 0.2)',
                      border: '1px solid #ff6b35',
                      color: '#ff6b35',
                      padding: window.innerWidth <= 480 ? '4px 6px' : '6px 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: window.innerWidth <= 480 ? '10px' : '12px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      minWidth: window.innerWidth <= 480 ? '50px' : '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                      minHeight: '32px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 107, 53, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 107, 53, 0.2)';
                    }}
                  >
                    ✏️ {window.innerWidth <= 480 ? '' : 'Edit'}
                  </button>

                  <button
                    onClick={() => handleDeleteClick(expense)}
                    disabled={deletingExpenseId === expense.id}
                    style={{
                      background: 'rgba(255, 71, 87, 0.2)',
                      border: '1px solid #ff4757',
                      color: '#ff4757',
                      padding: window.innerWidth <= 480 ? '4px 6px' : '6px 8px',
                      borderRadius: '6px',
                      cursor: deletingExpenseId === expense.id ? 'not-allowed' : 'pointer',
                      fontSize: window.innerWidth <= 480 ? '10px' : '12px',
                      fontWeight: '500',
                      opacity: deletingExpenseId === expense.id ? 0.5 : 1,
                      transition: 'all 0.2s ease',
                      minWidth: window.innerWidth <= 480 ? '50px' : '70px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                      minHeight: '32px'
                    }}
                    onMouseEnter={(e) => {
                      if (deletingExpenseId !== expense.id) {
                        e.target.style.background = 'rgba(255, 71, 87, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (deletingExpenseId !== expense.id) {
                        e.target.style.background = 'rgba(255, 71, 87, 0.2)';
                      }
                    }}
                  >
                    {deletingExpenseId === expense.id ? (
                      <>
                        <span style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid transparent',
                          borderTop: '2px solid #ff4757',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        {window.innerWidth <= 480 ? '' : 'Deleting...'}
                      </>
                    ) : (
                      <>🗑️ {window.innerWidth <= 480 ? '' : 'Delete'}</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      <ConfirmDialog
        isOpen={!!expenseToDelete}
        title="Delete Expense"
        message={`Are you sure you want to delete "${expenseToDelete?.description}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        type="danger"
      />

      {editingExpense && (
        <EditExpense
          expense={editingExpense}
          categories={categories}
          loadingCategories={false}
          accounts={accounts}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
}

export default ExpenseList;